import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend,
} from "recharts";
import { fetchCombined, fetchFarm } from "@/lib/api";
import { fmt } from "@/lib/risk";
import { stationColor } from "@/lib/stationColors";
import { cn } from "@/lib/utils";

const RANGES = [
    { key: "1h",  label: "1h"  },
    { key: "3h",  label: "3h"  },
    { key: "6h",  label: "6h"  },
    { key: "12h", label: "12h" },
    { key: "24h", label: "24h" },
    { key: "3d",  label: "3d"  },
    { key: "7d",  label: "7d"  },
];

const VARS = [
    { key: "temp",      label: "Temp",     unit: "°C",    axis: "temp", type: "line", solid: "#DC2626" },
    { key: "dew_point", label: "Dew",      unit: "°C",    axis: "temp", type: "line", solid: "#0EA5E9" },
    { key: "humidity",  label: "RH",       unit: "%",     axis: "rh",   type: "line", solid: "#16A34A" },
    { key: "wind",      label: "Wind",     unit: "km/h",  axis: "wind", type: "line", solid: "#7C3AED" },
    { key: "rain",      label: "Rain",     unit: "mm",    axis: "rain", type: "bar",  solid: "#2563EB" },
];

// Per-station colours come from lib/stationColors so every multi-station
// chart in the app uses an identical palette.

function tsFormatter(ts, range) {
    const d = new Date(ts * 1000);
    const opts = (range === "7d" || range === "3d")
        ? { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }
        : { hour: "2-digit", minute: "2-digit", hour12: false };
    return d.toLocaleString("en-AU", opts);
}

function shortFormatter(ts, range) {
    const d = new Date(ts * 1000);
    if (range === "7d") return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
    if (range === "3d") return d.toLocaleString("en-AU", { day: "2-digit", hour: "2-digit", hour12: false });
    return d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false });
}

export default function CombinedWeatherCard() {
    const [range, setRange] = useState("24h");
    const [station, setStation] = useState("all");
    const [hidden, setHidden] = useState(() => new Set());

    const { data: farm } = useQuery({ queryKey: ["farm"], queryFn: fetchFarm });
    const { data, isLoading } = useQuery({
        queryKey: ["combined", range, station],
        queryFn: () => fetchCombined(range, station),
        refetchInterval: 60_000,
    });

    // Flatten every station+variable into a merged row-per-ts for recharts.
    const { rows, seriesKeys } = useMemo(() => {
        if (!data?.stations) return { rows: [], seriesKeys: [] };
        const map = new Map();
        const keys = [];
        data.stations.forEach((st, sIdx) => {
            VARS.forEach((v) => {
                const key = `${st.id}__${v.key}`;
                keys.push({ ...v, seriesKey: key, stationId: st.id,
                    stationName: st.name, stroke: pickColor(v, st.id, sIdx, data.stations.length) });
                st.series.forEach((pt) => {
                    if (!map.has(pt.ts)) map.set(pt.ts, { ts: pt.ts });
                    map.get(pt.ts)[key] = pt[v.key];
                });
            });
        });
        const rows = Array.from(map.values()).sort((a, b) => a.ts - b.ts);
        return { rows, seriesKeys: keys };
    }, [data]);

    const stationOptions = useMemo(() => {
        const list = (farm?.stations || []).filter((s) => !s.is_placeholder);
        return [{ id: "all", name: "All Stations" }, ...list];
    }, [farm]);

    const toggle = (k) => {
        setHidden((h) => {
            const n = new Set(h);
            if (n.has(k)) n.delete(k); else n.add(k);
            return n;
        });
    };

    // When "All Stations" is selected, default to Temp only to avoid clutter —
    // user can enable others via the legend chips.
    const isAll = station === "all";
    const isVisible = (v) => {
        if (hidden.has(`var:${v.key}`)) return false;
        if (isAll && !hidden.has(`__initted__`)) {
            // initial state: only Temp visible when All Stations
            if (v.key !== "temp") return false;
        }
        return true;
    };
    // Once the user interacts with any legend chip we flip the __initted__
    // sentinel so their explicit choices persist.
    const isInitialAll = isAll && !hidden.has("__initted__");
    const varVisible = (vkey) => {
        if (isInitialAll && vkey !== "temp") return false;
        return !hidden.has(`var:${vkey}`);
    };

    return (
        <div className="fw-card p-4" data-testid="home-combined-weather">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                    <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
                        Weather over time
                    </h2>
                    <p className="text-[11px] text-slate-500 fw-metric">
                        Temp · Dew · RH · Wind · Rain
                        {data?.bucket_minutes ? ` · ${data.bucket_minutes}-min buckets` : ""}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        value={station}
                        onChange={(e) => setStation(e.target.value)}
                        data-testid="combined-station"
                        className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700"
                    >
                        {stationOptions.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                    <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
                        {RANGES.map((r) => (
                            <button key={r.key}
                                data-testid={`combined-range-${r.key}`}
                                onClick={() => setRange(r.key)}
                                className={cn(
                                    "px-2 py-1 text-[11px] font-bold rounded-md transition-colors",
                                    range === r.key
                                        ? "bg-white text-slate-900 shadow-sm"
                                        : "text-slate-500 hover:text-slate-800"
                                )}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Variable legend chips (click to hide/show) */}
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="combined-varlegend">
                {VARS.map((v) => {
                    const on = varVisible(v.key);
                    return (
                        <button key={v.key}
                            onClick={() => {
                                setHidden((h) => {
                                    const n = new Set(h);
                                    n.add("__initted__");
                                    const k = `var:${v.key}`;
                                    if (on) n.add(k); else n.delete(k);
                                    return n;
                                });
                            }}
                            data-testid={`combined-var-${v.key}`}
                            className={cn(
                                "inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider transition-colors",
                                on
                                    ? "border-slate-200 bg-white text-slate-800"
                                    : "border-slate-100 bg-slate-50 text-slate-400"
                            )}
                        >
                            <span className="h-2 w-2 rounded-full" style={{ background: on ? v.solid : "#CBD5E1" }} />
                            {v.label} <span className="opacity-60 font-normal normal-case">{v.unit}</span>
                        </button>
                    );
                })}
            </div>

            <div className="h-64 sm:h-72" data-testid="combined-chart">
                {isLoading || !rows.length ? (
                    <div className="h-full grid place-items-center text-xs text-slate-400">
                        {isLoading ? "Loading…" : "No data for this range"}
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={rows} margin={{ top: 8, right: 8, left: -18, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="ts" type="number" scale="time" domain={["dataMin", "dataMax"]}
                                tickFormatter={(v) => shortFormatter(v, range)}
                                tick={{ fontSize: 10 }} minTickGap={40} />
                            <YAxis yAxisId="temp" orientation="left" tick={{ fontSize: 10 }} width={30}
                                tickFormatter={(v) => `${v}°`} domain={["auto", "auto"]} />
                            <YAxis yAxisId="rh" orientation="right" tick={{ fontSize: 10 }} width={28}
                                tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                            <YAxis yAxisId="wind" orientation="right" tick={{ fontSize: 10 }} width={30}
                                tickFormatter={(v) => `${v}`} domain={[0, "auto"]}
                                axisLine={false} tickLine={false} hide />
                            <YAxis yAxisId="rain" orientation="right" tick={{ fontSize: 10 }} width={30}
                                tickFormatter={(v) => `${v}`} domain={[0, "auto"]}
                                axisLine={false} tickLine={false} hide />
                            <Tooltip
                                labelFormatter={(v) => tsFormatter(v, range)}
                                contentStyle={{ background: "white", border: "1px solid #e2e8f0",
                                    borderRadius: 12, fontSize: 12 }}
                                formatter={(value, name, props) => {
                                    if (value === null || value === undefined) return ["–", name];
                                    const meta = seriesKeys.find((k) => k.seriesKey === props.dataKey);
                                    const suffix = meta ? meta.unit : "";
                                    const label = meta ? (isAll
                                        ? `${meta.stationName} · ${meta.label}`
                                        : meta.label) : name;
                                    return [`${fmt(value, meta?.key === "humidity" ? 0 : 1)}${suffix}`, label];
                                }}
                            />
                            {seriesKeys.filter((k) => varVisible(k.key)).map((k) => (
                                k.type === "bar" ? (
                                    <Bar key={k.seriesKey}
                                        dataKey={k.seriesKey} yAxisId={k.axis}
                                        fill={k.stroke} opacity={0.55}
                                        isAnimationActive={false}
                                        maxBarSize={18} />
                                ) : (
                                    <Line key={k.seriesKey}
                                        type="monotone" dataKey={k.seriesKey}
                                        yAxisId={k.axis} stroke={k.stroke} strokeWidth={2}
                                        dot={false} activeDot={{ r: 3 }} connectNulls
                                        isAnimationActive={false} />
                                )
                            ))}
                        </ComposedChart>
                    </ResponsiveContainer>
                )}
            </div>

            {isAll && data?.stations?.length > 1 && (
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    {data.stations.map((st, i) => (
                        <div key={st.id} className="inline-flex items-center gap-1 text-[10px] text-slate-600 fw-metric">
                            <span className="h-2 w-2 rounded-full" style={{ background: stationColor(st.id, i) }} />
                            {st.name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function pickColor(variable, stationId, stationIdx, totalStations) {
    // Single station → variable's canonical colour so temp/dew/rh/wind/rain
    // remain individually distinguishable.
    if (totalStations <= 1) return variable.solid;
    // Multi-station → the station's canonical colour, identical across every
    // multi-station chart in the app (Weather-over-time, HFDI trend, YTD, …).
    return stationColor(stationId, stationIdx);
}

// mixHex kept for potential future use — currently unused because the
// station-colour palette itself is already high-contrast.
// eslint-disable-next-line no-unused-vars
function mixHex(a, b, w) {
    const pa = a.slice(1).match(/.{2}/g).map((h) => parseInt(h, 16));
    const pb = b.slice(1).match(/.{2}/g).map((h) => parseInt(h, 16));
    const mixed = pa.map((c, i) => Math.round(c * (1 - w) + pb[i] * w));
    return "#" + mixed.map((c) => c.toString(16).padStart(2, "0")).join("");
}
